import motion from '@lib/motion';

if (!customElements.get('localization-button')) {
  customElements.define(
    'localization-button',
    class extends HTMLButtonElement {
      static activeButton = null;

      constructor() {
        super();
        this.onButtonClick = this.onButtonClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.popoverElement = null;
        this.localizationItem = null;
        this.isOpen = false;
        this.animation = null;
        this.animationId = 0;
        this.popoverStyle = this.dataset.popoverStyle || 'background-color';
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onButtonClick);
        this.removeEventListener('keydown', this.onKeyDown);
        this.localizationItem?.removeEventListener('focusout', this.onFocusOut);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeyDown);
      }

      init() {
        this.localizationItem = this.closest('[data-localization-item]') || this.parentElement;
        this.removeEventListener('click', this.onButtonClick);
        this.removeEventListener('keydown', this.onKeyDown);
        this.localizationItem?.removeEventListener('focusout', this.onFocusOut);
        this.addEventListener('click', this.onButtonClick);
        this.addEventListener('keydown', this.onKeyDown);
        this.localizationItem?.addEventListener('focusout', this.onFocusOut);
      }

      get shouldReduceMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      extractPopover() {
        if (this.popoverElement?.isConnected) return this.popoverElement;

        if (this.dataset.extracted === 'true') {
          this.popoverElement = this.localizationItem?.querySelector('localization-popover');
          return this.popoverElement;
        }

        const trigger = this.dataset.trigger || this.dataset.triger;
        if (!trigger) return null;

        const template = document.querySelector(`template[data-template="${trigger}"]`);
        if (!template) return null;

        const content = template.content.cloneNode(true);
        const popover = content.querySelector('localization-popover');
        if (!popover) return null;

        popover.hidden = true;
        popover.dataset.state = 'closed';
        if (this.popoverStyle) {
          popover.querySelector('form').classList.add(...this.popoverStyle.split(/\s+/));
          // popover.classList.add(...this.popoverStyle.split(/\s+/));
        }
        this.insertAdjacentElement('afterend', popover);
        this.popoverElement = popover;
        this.dataset.extracted = 'true';

        return this.popoverElement;
      }

      onButtonClick(event) {
        event.preventDefault();

        if (this.isOpen) {
          this.closePopover();
        } else {
          this.openPopover();
        }
      }

      onDocumentClick(event) {
        if (!this.isOpen || this.localizationItem?.contains(event.target)) return;
        this.closePopover();
      }

      onDocumentKeyDown(event) {
        if (!this.isOpen || event.code.toUpperCase() !== 'ESCAPE') return;

        event.preventDefault();
        this.closePopover({ restoreFocus: true });
      }

      onFocusOut(event) {
        const nextElement = event.relatedTarget;

        requestAnimationFrame(() => {
          if (!this.isOpen) return;
          if (nextElement && this.localizationItem?.contains(nextElement)) return;
          if (this.localizationItem?.contains(document.activeElement)) return;

          this.closePopover();
        });
      }

      onKeyDown(event) {
        const code = event.code.toUpperCase();

        if (code === 'ENTER' || code === 'SPACE') {
          event.preventDefault();

          if (this.isOpen) {
            this.closePopover();
          } else {
            this.openPopover();
          }
        }
      }

      openPopover() {
        const popover = this.extractPopover();
        if (!popover) return;

        if (this.constructor.activeButton && this.constructor.activeButton !== this) {
          this.constructor.activeButton.closePopover();
        }

        this.animation?.cancel?.();
        this.animationId += 1;
        const currentAnimationId = this.animationId;

        popover.hidden = false;
        popover.dataset.state = 'opening';
        // popover.classList.add(this.popoverStyle);
        this.isOpen = true;
        this.constructor.activeButton = this;
        this.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeyDown);

        // Scale-only: opacity would make the opaque form see-through and reveal page content behind.
        this.animation = motion.animate(
          popover,
          {
            transform: ['scale(0)', 'scale(1)'],
          },
          { duration: this.shouldReduceMotion ? 0 : 0.2, easing: [0.22, 1, 0.36, 1] },
        );

        this.animation.finished
          .then(() => {
            if (currentAnimationId !== this.animationId || !this.isOpen) return;
            popover.dataset.state = 'open';
            popover.style.transform = 'none';

            requestAnimationFrame(() => {
              if (this.isOpen) popover.style.transform = '';
            });
          })
          .catch(() => { });
      }

      closePopover({ restoreFocus = false } = {}) {
        const popover = this.popoverElement;
        if (!this.isOpen || !popover) return;

        this.animation?.cancel?.();
        this.animationId += 1;
        const currentAnimationId = this.animationId;

        this.isOpen = false;
        this.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeyDown);

        if (this.constructor.activeButton === this) {
          this.constructor.activeButton = null;
        }

        popover.dataset.state = 'closing';

        this.animation = motion.animate(
          popover,
          {
            transform: ['scale(1)', 'scale(0)'],
          },
          { duration: this.shouldReduceMotion ? 0 : 0.16, easing: [0.4, 0, 1, 1] },
        );

        this.animation.finished
          .then(() => {
            if (currentAnimationId !== this.animationId || this.isOpen) return;
            popover.hidden = true;
            popover.dataset.state = 'closed';
            popover.style.transform = '';
            if (restoreFocus) this.focus();
          })
          .catch(() => { });
      }
    },
    { extends: 'button' },
  );
}

if (!customElements.get('localization-popover')) {
  customElements.define(
    'localization-popover',
    class LocalizationPopover extends HTMLElement {
      constructor() {
        super();
        this.searchInput = this.querySelector('input[name="country_filter"]');
        this.noResultsElement = this.querySelector('[data-no-results="true"]');
        this.onSearchKeyUp = this.onSearchKeyUp.bind(this);
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.searchInput?.removeEventListener('keyup', this.onSearchKeyUp);
      }

      init() {
        this.searchInput?.removeEventListener('keyup', this.onSearchKeyUp);
        this.searchInput?.addEventListener('keyup', this.onSearchKeyUp);
      }

      normalizeString(value) {
        return value
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase()
          .trim();
      }

      onSearchKeyUp() {
        const keyword = this.normalizeString(this.searchInput?.value || '');
        const countryItems = this.querySelectorAll('[data-country]');
        let visibleItemCount = 0;

        countryItems.forEach((item) => {
          const countryKeywords = this.normalizeString(item.dataset.country || '');
          const isHidden = Boolean(keyword) && !countryKeywords.includes(keyword);
          item.classList.toggle('hidden', isHidden);
          if (!isHidden) visibleItemCount += 1;
        });

        this.noResultsElement?.toggleAttribute('hidden', visibleItemCount > 0);
      }
    },
  );
}
